import React, { useState, useReducer, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import { Markup } from 'interweave';
import Spinner from '@strava/ui/Spinner';
import {
  InfiniteLoader,
  CellMeasurer,
  List,
  AutoSizer,
  CellMeasurerCache,
  WindowScroller
} from 'react-virtualized';
import { minBy, snakeCase } from 'lodash-es';
import tryUntil from 'simply-utils/dist/utils/tryUntil';
import styles from './feed.scss';

import Activity from './components/Activity';
import Post from './components/Post';
import FancyPromo from './components/FancyPromo';
import PerformanceGoalCreated from './components/PerformanceGoalCreated';
import ChallengeJoin from './components/ChallengeJoin';
import SimplePromo from './components/SimplePromo';
import GroupActivity from './components/GroupActivity';
import ClubJoin from './components/ClubJoin';

import { reducer } from './state/feed-reducer';

import { feedTypeProps, pageProps } from './props/feed-prop-types';
import useFetchFeedEntries from './hooks/useFetchFeedEntries';
import useFeedScrollRestoration from './hooks/useFeedScrollRestoration';
import FeedContext from './state/FeedContext';
import { devLog } from './utils';
import {
  UPDATE_FEED_ENTRY_PROPS,
  REGISTER_FEED_ENTRY_UI_CHANGE,
  UPDATE_IS_RESTORING_SCROLL_POSITION
} from './state/feed-actions';

import {
  ACTIVITY,
  CHALLENGE,
  CLUB,
  FANCY_PROMO,
  GROUP_ACTIVITY,
  PERFORMANCE_GOAL_CREATED,
  POST,
  SIMPLE_PROMO
} from './constants/FeedEntryTypes';

// Cursor pagination is key for the feed so this function has been deliberately
// exported so that it can be unit tested separately.
export const computeMinCursorData = (entries) => {
  return {
    minBeforeEntry: minBy(entries, (e) => {
      return e.cursorData.updated_at;
    }),
    minRankEntry: minBy(entries, (e) => {
      return e.cursorData.rank;
    })
  };
};

const Feed = ({
  currentAthleteId,
  page,
  feedType,
  athleteProfileId,
  clubId,
  useMockData,
  preFetchedEntries,
  locale
}) => {
  const {
    isLoading,
    error,
    entries,
    pagination,
    loadEntries
  } = useFetchFeedEntries({
    currentAthleteId,
    clubId,
    feedType,
    page,
    useMockData,
    preFetchedEntries
  });

  const [historyScrollTop, setHistoryScrollTop] = useState(0);

  const [entityState, dispatch] = useReducer(reducer, {
    uiUpdateToggle: false, // value toggles to signal a UI change that requires for a manual react-virtualized grid update
    props: {},
    // keeps track of entry impressions to avoid duplicate requests since
    // react-virtualized mounts the same component multiple times (see README for more info)
    ritmoRegisteredImpressions: [],
    numEntries: 0,
    scrollTop: 0,
    page,
    feedType,
    isUpdatingScrollPosition: false
  });

  const {
    saveScrollPosition,
    getScrollPosition,
    clearScrollPosition
  } = useFeedScrollRestoration({ ...entityState, scrollTop: historyScrollTop });

  const providerState = {
    entityState,
    dispatch
  };

  // cache stores the row heights needed for virtualized scrolling
  // {fixedWith = true} to enhance performance since the CellMeasurer is designed to
  // measure one dimension at a time.
  const [cache] = useState(new CellMeasurerCache({ fixedWidth: true }));

  // listRef is needed to update the sizes of the feed cards
  // in the feed list if they change
  const [listRef, setListRef] = useState(null);

  // AutoSizer#onResize gets calls even on the initial render
  // This keeps track of the initial render so that the list is not force updated the first time
  const [autoSizerInitialRender, setAutoSizerInitialRender] = useState(true);

  const initialRender = useRef(true);

  // If a specific feed entry has changed size then you need to clear its
  // cached size so it can be remeasured. (eg. clicking comments icon on an activity entry which opens the
  // mentionable athletes comments form)
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else if (!autoSizerInitialRender) {
      devLog('[feed] updating grid');
      cache.clearAll();
      listRef.forceUpdateGrid();
    }
    // only trigger when toggle changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityState.uiUpdateToggle]);

  // allow any positions within the threshold pixel values to be considered
  // as the final scroll position. This is done because react-virtualized does have an offset
  // for the y position based on whether it fetched rows while auto-scroll was in motion.
  const scrollPositionWithinRange = (scrollPosition) => {
    const threshold = 20;
    return (
      scrollPosition > window.scrollY - threshold &&
      scrollPosition < window.scrollY + threshold
    );
  };

  useEffect(() => {
    const scrollPosition = getScrollPosition();
    if (scrollPosition && listRef) {
      devLog(`[Feed] restoring scroll position to  ${scrollPosition}`);

      dispatch({
        type: UPDATE_IS_RESTORING_SCROLL_POSITION,
        payload: { status: true }
      });
      if (!scrollPositionWithinRange(scrollPosition)) {
        const numRetries = 10;
        const interval = 0;
        tryUntil(
          () => {
            listRef.scrollToPosition(scrollPosition);
            return scrollPositionWithinRange(scrollPosition);
          },
          numRetries,
          interval
        );
      }

      clearScrollPosition();
      dispatch({
        type: UPDATE_IS_RESTORING_SCROLL_POSITION,
        payload: { status: false }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRef]);

  // extracts entityId from entryProps
  // TODO - there is an opportunity to change backend response to be more uniform for all entries
  const getEntityId = (entryProps, index) => {
    let entityId = -1;
    const entityName = snakeCase(entryProps.entity);
    switch (entityName) {
      case ACTIVITY:
        entityId = entryProps.activity.id;
        break;
      case POST:
        entityId = entryProps.post.entity_id;
        break;
      case FANCY_PROMO:
      case SIMPLE_PROMO:
        // note: promos do not have entity id and instead are using trackable id as a unique identifier
        entityId = entryProps.rowData.trackable_id;
        break;
      case PERFORMANCE_GOAL_CREATED:
        entityId = entryProps.rowData.goal_entries[0].entity_id;
        break;
      case CHALLENGE:
      case CLUB:
        entityId = entryProps.rowData.entity_id;
        break;
      case GROUP_ACTIVITY:
        entityId = entryProps.rowData.activities[0].entity_id;
        break;
      default:
        devLog(`could not retrieve entity id for - ${entityName}`); // TODO: add sentry error logging in monorepo
    }
    // use {index} to generate unique entityIds for feed mocked data
    return useMockData
      ? `${entityId.toString()}-${index}`
      : entityId.toString();
  };

  // Updates entity state props when more entries are fetched
  useEffect(() => {
    devLog(
      `[Feed#useEffect-entries] updating feed-entry props | entry count = ${entries.length}`
    );
    entries.forEach((entryProps, index) => {
      const entityId = getEntityId(entryProps, index);
      // add new entries
      if (!entityState.props[entityId]) {
        dispatch({
          type: UPDATE_FEED_ENTRY_PROPS,
          payload: { entryProps, entityId }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  const { maxEntries, hasMore } = pagination;
  const i18nPrefix = 'components.feed';

  // return the proper feed card that can be rendered
  const renderEntry = (params) => {
    const { key, index } = params;
    const entryProps = Object.assign(entries[index], {
      athleteProfileId, // for analytics - when viewing feed on athlete page
      clubId, // for analytics - when viewing feed on club details page
      index,
      page,
      feedType,
      key,
      locale
    });

    let entry;
    const entityName = snakeCase(entryProps.entity);
    switch (entityName) {
      case ACTIVITY:
        entry = <Activity {...entryProps} />;
        break;
      case POST:
        entry = <Post {...entryProps} />;
        break;
      case FANCY_PROMO:
        entry = <FancyPromo {...entryProps} />;
        break;
      case PERFORMANCE_GOAL_CREATED:
        entry = <PerformanceGoalCreated {...entryProps} />;
        break;
      case CHALLENGE:
        entry = <ChallengeJoin {...entryProps} />;
        break;
      case SIMPLE_PROMO:
        entry = <SimplePromo {...entryProps} />;
        break;
      case GROUP_ACTIVITY:
        entry = <GroupActivity {...entryProps} />;
        break;
      case CLUB:
        entry = <ClubJoin {...entryProps} />;
        break;
      default:
        devLog(`Could not translate props into entry - ${entityName}`);
        entry = <div>N/A RF</div>; // TODO: Replace this with sentry error logging in monorepo
    }
    return (
      <div
        onClick={() => saveScrollPosition()}
        onKeyDown={() => saveScrollPosition()}
        role="button"
        tabIndex={0}
      >
        {entry}
      </div>
    );
  };

  const endMessage = () => {
    if (page === 'dashboard' && !isLoading) {
      const content = maxEntries
        ? `${i18nPrefix}.no_more.limit`
        : `${i18nPrefix}.no_more.days`;
      return (
        <div className={`${styles.feedPagination} ${styles.noEntries}`}>
          {I18n.t(content)}
          <br />
          <Markup
            noWrap={true}
            content={I18n.t(`${i18nPrefix}.no_more.full_html`, {
              athlete_id: currentAthleteId
            })}
          />
        </div>
      );
    }
    return null;
  };

  const loader = () => {
    return (
      <div className={styles.feedPagination}>
        <div className={styles.spinner}>
          {error ? I18n.t(`${i18nPrefix}.error_message`) : <Spinner />}
        </div>
      </div>
    );
  };

  // Invoked when more rows must be loaded.
  // Note: This function may be called multiple times in response to a single scroll event.

  // {startIndex} and {stopIndex} are react-virtualized API params that are not been used
  // by strava-feed but are necessary in this case for memoization.
  // eslint-disable-next-line no-unused-vars
  const loadMoreRows = ({ startIndex, stopIndex }) => {
    if (isLoading) {
      // avoids making api call before a response is received from previous calls
      return new Promise((resolve) => {
        resolve();
      });
    }

    let minBeforeEntry;
    let minRankEntry;
    if (entries.length > 0) {
      ({ minBeforeEntry, minRankEntry } = computeMinCursorData(entries));
    }
    const before = minBeforeEntry ? minBeforeEntry.cursorData.updated_at : null;
    const rank = minRankEntry ? minRankEntry.cursorData.rank : null;
    devLog(`[Feed#loadMoreRows] before: ${before} rank: ${rank}`);
    return new Promise((resolve) => {
      loadEntries({ cursorData: { before, rank } }).then(() => {
        resolve();
      });
    });
  };

  // This is a temporary fix to insert padding at the bottom of each card entry
  // TODO [fumba] - implement this in scss
  const cardEntryStyles = { paddingBottom: '6px' };

  // Number of card entries on the feed
  // If there are more items to be loaded, we add an extra row to hold a
  // loading indicator / spinner.
  const rowCount = (() => (hasMore ? entries.length + 1 : entries.length))();

  // responsible for tracking the loaded state of each row.
  const isRowLoaded = ({ index }) => {
    return !hasMore || index < entries.length;
  };

  // responsible for rendering a single card entry (row), given its index
  const rowRenderer = (params) => {
    // [ named parameters from react-virtualized ]
    // index - index of row
    // key - unique key within array of rows
    // style - style object to be applied to row
    // parent - reference to the parents list
    const { index, key, style, parent } = params;

    if (!isRowLoaded({ index })) {
      return null; // feed card has already been rendered
    }
    // automatically measures a cells content by temporarily rendering it in a way that is not visible to the user
    return (
      <div key={key} style={style}>
        {/* automatically measures a cells content by temporarily rendering it
        in a way that is not visible to the user */}
        <CellMeasurer
          cache={cache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {({ measure }) => (
            <div key={index} onLoad={measure} style={cardEntryStyles}>
              {renderEntry({ key, index })}
            </div>
          )}
        </CellMeasurer>
      </div>
    );
  };

  return (
    <FeedContext.Provider value={providerState}>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={rowCount}
      >
        {({ onRowsRendered, registerChild }) => (
          <WindowScroller
            onScroll={({ scrollTop }) => {
              setHistoryScrollTop(scrollTop);
            }}
          >
            {({ height, isScrolling, onChildScroll, scrollTop }) => (
              <AutoSizer
                disableHeight={true}
                onResize={() => {
                  if (autoSizerInitialRender) {
                    setAutoSizerInitialRender(false);
                  } else {
                    // force update components if onResize is called after autosizer was already initialized
                    dispatch({
                      type: REGISTER_FEED_ENTRY_UI_CHANGE
                    });
                  }
                }}
              >
                {({ width }) => (
                  <List
                    autoHeight={true}
                    deferredMeasurementCache={cache}
                    height={height}
                    onRowsRendered={onRowsRendered}
                    ref={(ref) => {
                      registerChild(ref);
                      setListRef(ref);
                    }}
                    rowCount={rowCount}
                    rowHeight={cache.rowHeight}
                    rowRenderer={rowRenderer}
                    width={width}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    scrollTop={scrollTop}
                    style={{ outline: 'none' }}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteLoader>
      {isLoading && loader()}
      {!hasMore && endMessage()}
    </FeedContext.Provider>
  );
};

Feed.propTypes = {
  currentAthleteId: PropTypes.number.isRequired,
  page: pageProps.isRequired,
  feedType: feedTypeProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  clubId: PropTypes.string.isRequired,
  useMockData: PropTypes.bool.isRequired,
  preFetchedEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locale: PropTypes.string.isRequired
};
export default Feed;
